/* Pet portrait configurator, shared rules engine.

   Two blocks present the same product in different layouts. Everything that
   decides what the customer is buying lives here, so the two views cannot
   drift apart: variant resolution, prices, the carry-across between sibling
   products, the size fallback and the add to cart gate.

   There is no DOM work in this file and no price constant. Prices are always
   read from the product's own variants, so an admin price edit flows through
   with no code change.

   Loaded by both blocks. The window guard makes the second load a no-op. */

(function () {
  'use strict';

  if (window.PiriConfigurator) return;

  var STORAGE_KEY = 'piri:configurator';
  var STORAGE_VERSION = 2;

  /* Set the moment the pet count switcher navigates, read and cleared on the
     next load. Referrer sniffing is not reliable enough to tell a sibling jump
     from a fresh visit, so the flag is explicit. */
  var JUMP_KEY = 'piri:configurator:jumped';

  /* The frame colour is part of the Format option value, not an option of its
     own. */
  var FRAME_TO_FORMAT = {
    none: 'Print only',
    White: 'Framed white',
    Wood: 'Framed wood',
    'Dark wood': 'Framed dark wood',
    Black: 'Framed black'
  };

  /* Money always renders through Intl, never a hand-built dollar sign. Whole
     dollar amounts drop the cents. */
  function money(cents) {
    var whole = cents % 100 === 0;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    }).format(cents / 100);
  }

  /* A signed delta, used by rows that price an upgrade rather than a total. */
  function delta(cents) {
    if (cents === 0) return '+' + money(0);
    return (cents > 0 ? '+' : '-') + money(Math.abs(cents));
  }

  function parseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function readStorage() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || data.v !== STORAGE_VERSION) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  /* Read once and clear, so a reload or a direct visit is never mistaken for
     a jump between siblings. */
  function takeJumpFlag() {
    try {
      var jumped = window.sessionStorage.getItem(JUMP_KEY) === '1';
      window.sessionStorage.removeItem(JUMP_KEY);
      return jumped;
    } catch (e) {
      return false;
    }
  }

  function setJumpFlag() {
    try {
      window.sessionStorage.setItem(JUMP_KEY, '1');
    } catch (e) {
      /* Storage unavailable. The destination simply shows no notice. */
    }
  }

  function Engine(config, product) {
    this.config = config;
    this.product = product;

    /* Option positions are looked up by name, never assumed, so a reordered
       option set in the admin cannot silently break resolution. */
    var names = (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
    this.formatIndex = names.indexOf('Format');
    this.sizeIndex = names.indexOf('Size');
    /* Background is a real option but stays optional, a product without it
       still resolves. */
    this.backgroundIndex = names.indexOf('Background');

    /* Set by a view that swaps in place instead of navigating, since that is
       what decides whether chosen photos can be carried over. */
    this.photosSurvive = false;

    this.state = null;
  }

  Engine.prototype.isUsable = function () {
    return this.formatIndex !== -1 && this.sizeIndex !== -1;
  };

  /* Build the opening state and report what the view should announce.
     Precedence: stored state beats metafield defaults, the query parameter
     beats both. The page's own pet count always wins, it is a fact of the
     product rather than a preference. */
  Engine.prototype.init = function () {
    var config = this.config;
    var defaults = config.default || {};

    this.state = {
      pets: config.pets,
      format: defaults.format || 'print',
      size: defaults.size,
      frame: defaults.frame || 'none',
      background: defaults.background || 'White',
      petNames: ['', '', ''],
      petPhotos: [false, false, false]
    };

    var stored = readStorage();
    if (stored) {
      this.state.format = stored.format || this.state.format;
      this.state.size = stored.size || this.state.size;
      this.state.frame = stored.frame || this.state.frame;
      this.state.background = stored.background || this.state.background;
      if (Array.isArray(stored.petNames)) {
        this.state.petNames = stored.petNames.slice(0, 3);
        while (this.state.petNames.length < 3) this.state.petNames.push('');
      }
      if (Array.isArray(stored.petPhotos)) {
        this.state.petPhotos = stored.petPhotos.slice(0, 3);
        while (this.state.petPhotos.length < 3) this.state.petPhotos.push(false);
      }
    }

    var jumped = takeJumpFlag();

    /* One directional size fallback. A stored size the destination does not
       offer becomes the destination's default, and this one is always
       announced because the customer's choice changed under them. */
    var fallbackMessage = '';
    if ((config.sizes || []).indexOf(this.state.size) === -1) {
      var was = this.state.size;
      this.state.size = (config.default && config.default.size) || (config.sizes && config.sizes[0]);
      if (was) {
        fallbackMessage = 'size changed from ' + was + ' to ' + this.state.size + '. ' + was + " isn't offered for " + config.pets + ' pets.';
      }
    }

    /* The notice lists only what the customer actually set, so a fresh visit
       or an untouched default never produces one. */
    var carryItems = [];
    if (jumped && stored) {
      var shared = Math.min(stored.pets || 0, config.pets);
      var hasNames = this.state.petNames.slice(0, shared).some(function (v) { return v; });
      var hasPhotos = this.state.petPhotos.slice(0, shared).some(function (v) { return v; });
      var defaultBackground = defaults.background || 'White';
      if (hasNames) carryItems.push('names');
      /* Photos survive a swap, where the page never unloads. On the fallback
         path, a real navigation, they cannot be restored, so the view tells
         the engine which wording applies. */
      if (hasPhotos) carryItems.push(this.photosSurvive ? 'photos' : 'photos need attaching again');
      if (this.state.background !== defaultBackground) {
        carryItems.push('background (' + this.state.background.toLowerCase() + ')');
      }
    }
    if (fallbackMessage) carryItems.push(fallbackMessage);

    var notice = '';
    if (carryItems.length) {
      notice = (jumped && carryItems.length > (fallbackMessage ? 1 : 0))
        ? 'Carried across: ' + carryItems.join(' · ')
        : carryItems.join(' · ');
    }

    /* The spec asked for format=digital, but format is a reserved Shopify
       storefront parameter that 404s the page, so the flag rides as
       fmt=digital instead. */
    if (window.location.search.indexOf('fmt=digital') > -1) {
      this.state.format = 'digital';
    }

    /* A link that names a variant (the homepage cards, for one) opens the page
       on that exact selection: digital file, frame finish, size and background
       all follow it. It beats stored state, since the link is the latest
       thing the customer chose. */
    var linkedVariant = new URLSearchParams(window.location.search).get('variant');
    if (linkedVariant) {
      var variants = this.product.variants || [];
      for (var v = 0; v < variants.length; v++) {
        if (String(variants[v].id) === String(linkedVariant)) {
          var linked = this.stateForVariant(variants[v]);
          var self = this;
          Object.keys(linked).forEach(function (key) { self.state[key] = linked[key]; });
          if (linked.format === 'digital') this.state.format = 'digital';
          break;
        }
      }
    }

    return { notice: notice, jumped: jumped };
  };

  Engine.prototype.persist = function () {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        v: STORAGE_VERSION,
        pets: this.state.pets,
        format: this.state.format,
        size: this.state.size,
        frame: this.state.frame,
        background: this.state.background,
        petNames: this.state.petNames,
        petPhotos: this.state.petPhotos
      }));
    } catch (e) {
      /* Storage can be unavailable in private modes. The configurator still
         works, state just stops carrying across pages. */
    }
  };

  Engine.prototype.set = function (patch) {
    var self = this;
    Object.keys(patch).forEach(function (key) { self.state[key] = patch[key]; });
    this.persist();
  };

  /* ---------- variant resolution ---------- */

  Engine.prototype.formatValue = function () {
    if (this.state.format === 'digital') return 'Digital file';
    return FRAME_TO_FORMAT[this.state.frame] || 'Print only';
  };

  Engine.prototype.sizeValue = function () {
    return this.state.format === 'digital' ? 'Digital' : this.state.size;
  };

  /* Background is matched when the product carries that option and a value is
     given. Passing no background finds the first variant at that format and
     size, which is what the price lookups want. */
  Engine.prototype.variantFor = function (formatValue, sizeValue, backgroundValue) {
    var variants = this.product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options;
      if (options[this.formatIndex] !== formatValue) continue;
      if (options[this.sizeIndex] !== sizeValue) continue;
      if (backgroundValue && this.backgroundIndex !== -1 && options[this.backgroundIndex] !== backgroundValue) continue;
      return variants[i];
    }
    return null;
  };

  Engine.prototype.resolveVariant = function () {
    return this.variantFor(this.formatValue(), this.sizeValue(), this.state.background);
  };

  Engine.prototype.digitalPrice = function () {
    var variant = this.variantFor('Digital file', 'Digital', this.state.background);
    return variant ? variant.price : 0;
  };

  /* Price of the unframed print at a size. */
  Engine.prototype.sizePriceCents = function (size) {
    var variant = this.variantFor('Print only', size, this.state.background);
    return variant ? variant.price : 0;
  };

  /* The cheapest print, used for a from price. */
  Engine.prototype.fromPriceCents = function () {
    var sizes = this.config.sizes || [];
    var lowest = 0;
    for (var i = 0; i < sizes.length; i++) {
      var price = this.sizePriceCents(sizes[i]);
      if (price && (lowest === 0 || price < lowest)) lowest = price;
    }
    return lowest;
  };

  /* Frame add-on at a size is the framed price minus the print only price at
     the same size. */
  Engine.prototype.frameAddCents = function (size) {
    var background = this.state.background;
    var printOnly = this.variantFor('Print only', size, background);
    var framed = this.variantFor('Framed white', size, background);
    if (printOnly && framed) return framed.price - printOnly.price;
    return 0;
  };

  /* What a size costs relative to the cheapest one, for rows that price an
     upgrade rather than a total. */
  Engine.prototype.sizeDeltaCents = function (size) {
    var base = this.fromPriceCents();
    var price = this.sizePriceCents(size);
    if (!base || !price) return 0;
    return price - base;
  };

  /* Map a variant back to state, used when another control on the page picks
     a variant directly. */
  Engine.prototype.stateForVariant = function (variant) {
    var self = this;
    var formatValue = variant.options[this.formatIndex];
    var patch = {};
    if (this.backgroundIndex !== -1) {
      patch.background = variant.options[this.backgroundIndex];
    }
    if (formatValue === 'Digital file') {
      patch.format = 'digital';
      return patch;
    }
    var frame = 'none';
    Object.keys(FRAME_TO_FORMAT).forEach(function (key) {
      if (FRAME_TO_FORMAT[key] === formatValue) frame = key;
    });
    patch.format = 'print';
    patch.frame = frame;
    patch.size = variant.options[self.sizeIndex];
    return patch;
  };

  /* Keep the address bar true to the selection, including after the theme's
     own replaceState wiped the format flag. */
  Engine.prototype.syncUrl = function (variantId) {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    params.set('variant', variantId);
    if (this.state.format === 'digital') {
      params.set('fmt', 'digital');
    } else {
      params.delete('fmt');
    }
    window.history.replaceState({}, '', window.location.pathname + '?' + params.toString());
  };

  /* The href a sibling link should actually go to, carrying the digital flag. */
  Engine.prototype.siblingHref = function (href) {
    return this.state.format === 'digital' ? href + '?fmt=digital' : href;
  };

  /* Why add to cart is blocked, or an empty string when it is not. The view
     supplies the photo lookup because only it knows its own markup. */
  Engine.prototype.gateMessage = function (variant, missingPhotoLabel, photoTemplate) {
    if (!variant) return 'That combination is not available. Try a different option.';
    if (variant.available === false) return 'This combination is sold out.';
    if (missingPhotoLabel) {
      var template = photoTemplate || 'Add a photo for [pet] to continue.';
      return template.replace('[pet]', missingPhotoLabel);
    }
    return '';
  };

  window.PiriConfigurator = {
    STORAGE_KEY: STORAGE_KEY,
    STORAGE_VERSION: STORAGE_VERSION,
    JUMP_KEY: JUMP_KEY,
    FRAME_TO_FORMAT: FRAME_TO_FORMAT,
    money: money,
    delta: delta,
    parseJSON: parseJSON,
    readStorage: readStorage,
    setJumpFlag: setJumpFlag,
    createEngine: function (config, product) {
      return new Engine(config, product);
    }
  };
})();
